// Appends UTM parameters and a per-visit gck_id to every external link click
// before the browser navigates away, and reports the click to analytics.
// Runs once at app startup via initOutboundTracking().

import { generateId } from './ids';
import { trackEvent } from './analytics';

// In-memory only, like the analytics client ID: the app writes nothing to the
// visitor's device, which is what keeps the privacy policy true and the site
// free of a consent banner. Lives as long as this JS runtime -- which, for an
// SPA, is the whole visit.
const sessionId = generateId();

function isExternal(href: string): boolean {
  try {
    const url = new URL(href, window.location.href);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function appendTrackingParams(href: string): string {
  try {
    const url = new URL(href);
    // Don't clobber existing utm_source if the destination already has one.
    if (!url.searchParams.has('utm_source')) {
      url.searchParams.set('utm_source', 'globalcartkit');
    }
    if (!url.searchParams.has('utm_medium')) {
      url.searchParams.set('utm_medium', 'referral');
    }
    if (!url.searchParams.has('utm_campaign')) {
      url.searchParams.set('utm_campaign', 'gck-outbound');
    }
    // gck_id is always set — it's our identifier, not a standard UTM.
    url.searchParams.set('gck_id', sessionId);
    return url.toString();
  } catch {
    return href;
  }
}

// Host + path only. The full href would carry gck_id into a GA4 dimension,
// which is both a per-visit identifier we don't want there and unbounded
// cardinality that would collapse the dimension into "(other)".
function cleanDestination(href: string): string | undefined {
  try {
    const url = new URL(href, window.location.href);
    return `${url.host}${url.pathname}`.replace(/\/$/, '');
  } catch {
    return undefined;
  }
}

export function initOutboundTracking(): void {
  if (typeof window === 'undefined') return;

  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Walk up the DOM to find the nearest <a> tag (handles clicks on child elements).
      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || !isExternal(href)) return;

      // Report before rewriting, so the tracking params never reach analytics.
      trackEvent('outbound_click', { destination: cleanDestination(href) });

      // Modify the href in place so the browser follows the updated URL.
      // Works for both target="_blank" and same-tab navigation.
      const tracked = appendTrackingParams(href);
      if (tracked !== href) {
        anchor.setAttribute('href', tracked);
        // Restore original after a tick so repeated clicks on the same link
        // don't double-stack params.
        setTimeout(() => anchor.setAttribute('href', href), 0);
      }
    },
    // Capture phase so we modify href before the browser acts on the click.
    true
  );
}
